import { coerceDateTime, memberRoleLabel } from '../my-journeys/myJourneysModel';

export const INVITATION_INTENDED_ROLES = Object.freeze(['MEMBER', 'COORDINATOR']);
export const INVITATION_ACTIONABLE_STATUSES = Object.freeze(['PENDING']);

export function isPositiveId(value) {
  return /^[1-9]\d*$/.test(String(value ?? '').trim());
}

export function asIdString(value) {
  if (value == null || value === '') return '';
  return String(value);
}

function pad(value) {
  return String(value).padStart(2, '0');
}

export function toLocalDateTimeInput(date = new Date()) {
  return [
    date.getFullYear(),
    '-',
    pad(date.getMonth() + 1),
    '-',
    pad(date.getDate()),
    'T',
    pad(date.getHours()),
    ':',
    pad(date.getMinutes()),
  ].join('');
}

export function defaultInvitationExpiresAt(now = new Date()) {
  return toLocalDateTimeInput(new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000));
}

export function toBackendExpiresAt(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(raw)) return `${raw}:00`;
  return raw;
}

export function invitationStatusLabel(status) {
  const labels = {
    PENDING: '待回覆',
    ACCEPTED: '已接受',
    DECLINED: '已拒絕',
    REVOKED: '已撤回',
    EXPIRED: '已過期',
  };
  return labels[status] || String(status || '');
}

export function intendedRoleLabel(role) {
  return memberRoleLabel(role) || String(role || '');
}

export function canRevokeInvitation(invitation, { canInvite } = {}) {
  return Boolean(canInvite) && invitation?.status === 'PENDING';
}

export function normalizeInvitation(value) {
  const invitationId = asIdString(value?.invitationId);
  const journeyId = asIdString(value?.journeyId);
  const recipientMemberId = asIdString(value?.recipientMemberId ?? value?.memberId);
  const inviterMemberId = asIdString(value?.inviterMemberId);
  const intendedRole = value?.intendedRole || value?.membershipRole || '';
  const status = value?.finalStatus || value?.invitationStatus || value?.status || '';
  return {
    invitationId,
    journeyId,
    journeyTitle: String(value?.journeyTitle || ''),
    inviterMemberId,
    recipientMemberId,
    intendedRole,
    intendedRoleLabel: intendedRoleLabel(intendedRole),
    status,
    statusLabel: invitationStatusLabel(status),
    invitedAt: coerceDateTime(value?.invitedAt) || '',
    expiresAt: coerceDateTime(value?.expiresAt) || '',
    acceptedAt: coerceDateTime(value?.acceptedAt) || '',
    declinedAt: coerceDateTime(value?.declinedAt) || '',
    revokedAt: coerceDateTime(value?.revokedAt) || '',
    membershipRole: value?.membershipRole || '',
    journeyRowVersion: value?.journeyRowVersion ?? null,
    memberId: asIdString(value?.memberId),
  };
}

export function normalizeInvitationInboxPage(payload) {
  const items = (Array.isArray(payload?.items) ? payload.items : []).map(normalizeInvitation)
    .filter((row) => isPositiveId(row.invitationId));
  const cursorInvitationId = asIdString(payload?.nextCursor?.cursorInvitationId);
  return {
    items,
    hasMore: Boolean(payload?.hasMore),
    nextCursor: payload?.hasMore && payload?.nextCursor?.cursorInvitedAt && isPositiveId(cursorInvitationId)
      ? {
        cursorInvitedAt: coerceDateTime(payload.nextCursor.cursorInvitedAt) || payload.nextCursor.cursorInvitedAt,
        cursorInvitationId,
      }
      : null,
  };
}

export function partitionInvitationInbox(items) {
  const pending = [];
  const history = [];
  (Array.isArray(items) ? items : []).forEach((item) => {
    if (item?.status === 'PENDING') pending.push(item);
    else history.push(item);
  });
  return { pending, history };
}

export function canMutateIncomingInvitation(invitation) {
  return invitation?.status === 'PENDING' && isPositiveId(invitation?.invitationId);
}

export function invitationErrorMessage(cause) {
  const key = String(cause?.raw?.key || '');
  const message = String(cause?.message || '');
  const lower = message.toLowerCase();

  if (key === 'travel.journey.invitation.conflict') {
    if (lower.includes('already active')) return '該成員已是 ACTIVE 旅伴。';
    if (lower.includes('pending invitation already exists')) return '此旅程已有尚未完成的邀請。';
    if (lower.includes('expired')) return '這張邀請已過期。';
    if (lower.includes('must be pending')) return '邀請狀態已不可操作。';
    if (lower.includes('does not allow')) return '目前旅程狀態不允許此邀請操作。';
    if (lower.includes('reactivation')) return '已 REMOVED 的成員無法再透過邀請加入。';
    return message || '邀請狀態衝突。';
  }
  if (key === 'travel.journey.permission.denied') {
    return '沒有權限執行此邀請操作。';
  }
  if (key === 'travel.journey.not.found') {
    if (lower.includes('member')) return '找不到此 memberId。';
    if (lower.includes('invitation')) return '找不到這張邀請。';
    return '找不到邀請或旅程。';
  }
  if (key === 'travel.journey.validation.failed') {
    if (lower.includes('cannot invite self')) return '不能邀請自己。';
    if (lower.includes('expir')) return '到期時間必須晚於現在。';
    if (lower.includes('role')) return '邀請角色只能是 COORDINATOR 或 MEMBER。';
    return message || '邀請資料不正確。';
  }
  return message || '邀請操作失敗。';
}

export function buildCreateInvitationBody({ recipientMemberId, intendedRole, expiresAt }) {
  return {
    recipientMemberId: String(recipientMemberId).trim(),
    intendedRole,
    expiresAt: toBackendExpiresAt(expiresAt),
  };
}
